import { useMemo } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { MessageSquare } from "lucide-react"
import { DmThread } from "@/components/conversation/DmThread"
import { useActiveEvent } from "@/core/active-event"
import { conversationsQueryOptions } from "@/core/conversations"

export const Route = createFileRoute("/$orgSlug/$eventSlug/d/$conversationId")({
  component: DmThreadView,
})

// Full-screen DM thread — the narrow-canvas counterpart to the conversations
// reading-pane split. Resolves the conversation from the DM list (same cache
// key as the list view).
function DmThreadView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/d/$conversationId" })
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()

  const dmsQ = useQuery({
    ...conversationsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const conversation = useMemo(
    () => (dmsQ.data ?? []).find((c) => c.id === params.conversationId) ?? null,
    [dmsQ.data, params.conversationId],
  )

  const backToList = () =>
    navigate({
      to: "/$orgSlug/$eventSlug/conversations",
      params: { orgSlug: params.orgSlug, eventSlug: params.eventSlug },
    })

  if (dmsQ.isLoading) {
    // Mirror the loaded DmThread frame (header h-[52px] + canvas message area +
    // composer footer) so the route doesn't flash a bare centered spinner before
    // <DmThread> mounts. Restrained grey pulse placeholders, house style.
    return (
      <div className="flex h-full flex-col bg-surface">
        <header className="flex h-[52px] shrink-0 items-center gap-2.5 border-b border-plum-200/50 pr-1.5 pl-3 dark:border-white/10">
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
          <div className="min-w-0 space-y-1.5">
            <div className="h-3.5 w-32 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
            <div className="h-2.5 w-20 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
          </div>
        </header>
        <div className="canvas-bg flex-1 overflow-hidden px-4 py-4 sm:px-5">
          <div className="mx-auto flex w-full max-w-[760px] flex-col gap-5">
            {[44, 32, 56, 28].map((bodyW, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-3 w-28 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                  <div
                    className="h-3 animate-pulse rounded bg-plum-100 dark:bg-white/10"
                    style={{ width: `${bodyW}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="shrink-0 border-t border-plum-200/50 p-3 dark:border-white/10">
          <div className="h-11 w-full animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
        </div>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-sm text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
              <MessageSquare className="h-5 w-5" />
            </div>
            <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
              Conversation not found
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
              It may have been archived, or you may no longer be a member.
            </p>
            <button
              onClick={backToList}
              className="mt-4 rounded-lg px-3.5 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110"
              style={{ background: "var(--accent)" }}
            >
              Back to messages
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DmThread
      key={conversation.id}
      conversation={conversation}
      onBack={backToList}
      closeVariant="back"
    />
  )
}
